// CompanyHistory.tsx
import { useState } from 'react'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { useGetCompanyWideRequestsQuery } from '../../../../features/vacations/vacationsApiSlice'
import useOwnUser from '../../../../hooks/useOwnUser'
import { selectCurrentToken } from '../../../../features/auth/authSlice'
import { t } from 'i18next'
import axios from 'axios'
import { appConfig } from '../../../../config'
import { IVacationRequest } from '@/pkg/requests'
import RejectionDialog from './components/RejectDialog'
import ApprovalDialog from './components/ApprovalDialog'
import CancellationDialog from './components/ CancelDialog'
import { FaFilePdf } from 'react-icons/fa6'
import { styled } from 'styled-components'
import {
  Container,
  MonthSection,
  MonthHeader,
  MonthTitle,
  ReportButton,
  EmptyState,
  ApproveIcon,
  DenyIcon,
  CancelIcon,
} from './historyElements'
import Tables from './../../../root/components/Table'

// Styled components dla statusów
const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 16px;
  background-color: ${({ $status }) =>
    $status === 'pending' ? '#fff8e1'
    : $status === 'approved' ? '#e8f5e9'
    : $status === 'rejected' ? '#ffebee'
    : $status === 'cancelled' ? '#e3f2fd'
    : '#f5f5f5'};
  color: ${({ $status }) =>
    $status === 'pending' ? '#f57c00'
    : $status === 'approved' ? '#2e7d32'
    : $status === 'rejected' ? '#c62828'
    : $status === 'cancelled' ? '#1565c0'
    : '#757575'};
  border: 1px solid
    ${({ $status }) =>
      $status === 'pending' ? '#ffe082'
      : $status === 'approved' ? '#a5d6a7'
      : $status === 'rejected' ? '#ef9a9a'
      : $status === 'cancelled' ? '#90caf9'
      : '#e0e0e0'};
`

const ActionButton = styled.button<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  color: white;
  border: none;
  cursor: pointer;
  margin-right: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
`

const DateCell = styled.span`
  font-family: monospace;
  background-color: #f9f9f9;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.9rem;
`

const TypeBadge = styled.span<{ $type: string }>`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  background-color: ${({ $type }) =>
    $type === 'recreational' ? '#e8f5e9'
    : $type === 'ondemand' ? '#e3f2fd'
    : $type === 'occasional' ? '#fff8e1'
    : $type === 'parental' ? '#f3e5f5'
    : $type === 'unpaid' ? '#f5f5f5'
    : $type === 'l4' ? '#ffebee'
    : '#f5f5f5'};
  color: ${({ $type }) =>
    $type === 'recreational' ? '#2e7d32'
    : $type === 'ondemand' ? '#1565c0'
    : $type === 'occasional' ? '#f57c00'
    : $type === 'parental' ? '#7b1fa2'
    : $type === 'unpaid' ? '#757575'
    : $type === 'l4' ? '#c62828'
    : '#757575'};
`

const HoursDisplay = styled.span`
  font-weight: 500;
  font-size: 0.95rem;
`

const UserName = styled.span`
  font-weight: 500;
`

interface GroupedRequests {
  [key: string]: IVacationRequest[]
}

// Extend the IVacationRequest interface locally to add created_at
interface ExtendedVacationRequest extends IVacationRequest {
  created_at: string
}

const CompanyHistory: React.FC = () => {
  const sessionToken = useSelector(selectCurrentToken)
  const user = useOwnUser()

  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState<boolean>(false)
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState<boolean>(false)
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] = useState<boolean>(false)
  const [selectedRequestID, setSelectedRequestID] = useState<number>(0)
  const [info, setInfo] = useState<any>(null)

  const {
    data: requests,
    isSuccess,
    refetch,
  } = useGetCompanyWideRequestsQuery(
    { companyID: user.selectedCompanyID ?? 0 }, // Add fallback value
    { skip: user.selectedCompanyID === undefined },
  )

  const columns = [
    { header: t('CompanyHistory:History.fullName'), accessor: 'full_name' },
    { header: t('CompanyHistory:History.type'), accessor: 'type' },
    { header: t('CompanyHistory:History.startingDate'), accessor: 'starting_date' },
    { header: t('CompanyHistory:History.finishingDate'), accessor: 'finishing_date' },
    { header: t('CompanyHistory:History.hours'), accessor: 'hours' },
    { header: t('CompanyHistory:History.createdAt'), accessor: 'created_at' },
    { header: t('CompanyHistory:History.status'), accessor: 'status' },
    { header: t('CompanyHistory:History.options'), accessor: 'options' },
  ]

  const groupRequestsByMonth = (requests: ExtendedVacationRequest[]): GroupedRequests => {
    const grouped: GroupedRequests = {}
    requests.forEach((request) => {
      const month = dayjs(request.starting_date).format('MMMM YYYY')
      if (!grouped[month]) {
        grouped[month] = []
      }
      grouped[month].push(request)
    })
    return grouped
  }

  let groupedRequests =
    isSuccess && requests ? groupRequestsByMonth(requests as ExtendedVacationRequest[]) : {}

  const sortedMonths = Object.keys(groupedRequests).sort((a, b) => {
    const dateA = dayjs(a, 'MMMM YYYY')
    const dateB = dayjs(b, 'MMMM YYYY')

    if (dateA.year() !== dateB.year()) {
      return dateB.year() - dateA.year()
    }
    return dateB.month() - dateA.month()
  })

  const handleGetPDF = (month: string): void => {
    if (!user.selectedCompanyID) return // Add early return if undefined

    const date = new Date(`${month.split(' ')[0]} 1, 2000`)
    const monthNumber = date.getMonth() + 1
    axios
      .get(`${appConfig.SERVER_URL}/vacation/month-summary`, {
        params: {
          company_id: user.selectedCompanyID,
          month: monthNumber,
          year: month.split(' ')[1],
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
      })
      .then((res: any) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `summary.pdf`)
        document.body.appendChild(link)
        link.click()
      })
  }

  const openApprovalDialog = (requestId: number | undefined, requestInfo: any) => {
    if (requestId === undefined) return

    setIsApprovalDialogOpen(true)
    setSelectedRequestID(requestId)
    setInfo(requestInfo)
  }

  const openRejectionDialog = (requestId: number | undefined, requestInfo: any) => {
    if (requestId === undefined) return

    setIsRejectionDialogOpen(true)
    setSelectedRequestID(requestId)
    setInfo(requestInfo)
  }

  const openCancellationDialog = (requestId: number | undefined, requestInfo: any) => {
    if (requestId === undefined) return

    setIsCancellationDialogOpen(true)
    setSelectedRequestID(requestId)
    setInfo(requestInfo)
  }

  const cellRenderer = (dataKey: string, row: any): JSX.Element => {
    if (dataKey === 'starting_date' || dataKey === 'finishing_date' || dataKey === 'created_at') {
      return <DateCell>{formatDate(row[dataKey])}</DateCell>
    }

    if (dataKey === 'type') {
      return <TypeBadge $type={row[dataKey]}>{getVacationType(row[dataKey])}</TypeBadge>
    }

    if (dataKey === 'status') {
      return (
        <StatusBadge $status={row.status}>
          {t('historyVacation:HistoryUser.' + row[dataKey])}
        </StatusBadge>
      )
    }

    if (dataKey === 'options') {
      return (
        <ActionContainer>
          {row.status === 'pending' && (
            <>
              <ActionButton
                $color='#4caf50'
                onClick={() => openApprovalDialog(row.id, row)}
                title={t('CompanyHistory:History.approve')}
              >
                <ApproveIcon />
              </ActionButton>
              <ActionButton
                $color='#f44336'
                onClick={() => openRejectionDialog(row.id, row)}
                title={t('CompanyHistory:History.reject')}
              >
                <DenyIcon />
              </ActionButton>
            </>
          )}
          {row.status === 'approved' && (
            <ActionButton
              $color='#2196f3'
              onClick={() => openCancellationDialog(row.id, row)}
              title={t('CompanyHistory:History.cancel')}
            >
              <CancelIcon />
            </ActionButton>
          )}
        </ActionContainer>
      )
    }

    if (dataKey === 'full_name') {
      return <UserName>{row[dataKey]}</UserName>
    }

    if (dataKey === 'hours') {
      return <HoursDisplay>{row[dataKey]} h</HoursDisplay>
    }

    return <span>{row[dataKey]}</span>
  }

  return (
    <Container>
      {sortedMonths.length > 0 ?
        sortedMonths.map((month) => (
          <MonthSection key={month}>
            <MonthHeader>
              <MonthTitle>
                {t('Root:' + month.split(' ')[0]) + ' ' + month.split(' ')[1]}
              </MonthTitle>
              <ReportButton onClick={() => handleGetPDF(month)}>
                <FaFilePdf />
                {t('historyVacation:HistoryUser.getRaport')}
              </ReportButton>
            </MonthHeader>
            <Tables
              columns={columns}
              data={groupedRequests[month]}
              cellRenderer={cellRenderer}
            />
          </MonthSection>
        ))
      : <EmptyState>
          <p>{t('CompanyHistory:History.noRequests')}</p>
        </EmptyState>
      }

      <RejectionDialog
        isOpen={isRejectionDialogOpen}
        handleClose={() => setIsRejectionDialogOpen(false)}
        request={info}
        refetch={refetch}
      />
      <ApprovalDialog
        isOpen={isApprovalDialogOpen}
        handleClose={() => setIsApprovalDialogOpen(false)}
        request={info}
        refetch={refetch}
      />
      <CancellationDialog
        isOpen={isCancellationDialogOpen}
        handleClose={() => setIsCancellationDialogOpen(false)}
        request={info}
        refetch={refetch}
      />
    </Container>
  )
}

export default CompanyHistory

const formatDate = (date: string): string => {
  return dayjs(date).format('YYYY-MM-DD')
}

const getVacationType = (key: string): string => {
  switch (key) {
    case 'ondemand':
      return t('CompanyHistory:History.ondemand')
    case 'recreational':
      return t('CompanyHistory:History.recreational')
    case 'occasional':
      return t('CompanyHistory:History.occasional')
    case 'parental':
      return t('CompanyHistory:History.parental')
    case 'unpaid':
      return t('CompanyHistory:History.unpaid')
    case 'l4':
      return t('CompanyHistory:History.l4')
    default:
      return t('CompanyHistory:History.default')
  }
}
